import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { 
  MapPin, 
  Fish, 
  Ruler, 
  Waves, 
  Star, 
  Landmark, 
  ExternalLink,
  ChevronDown,
  ChevronUp,
  Phone,
  AlertTriangle,
  Compass,
  Link as LinkIcon
} from 'lucide-react';
import DashboardPageLayout from './DashboardPageLayout';

const LakeAustinInfo = () => {
  const [expandedSections, setExpandedSections] = useState({
    overview: true,
    landmarks: false,
    wildlife: false,
    celebrities: false,
    stories: false
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const lakeInfo = {
    overview: {
      length: "21 miles",
      maxDepth: "75 feet",
      averageDepth: "12-20 feet",
      surface: "1,830 acres",
      created: "1939 (Tom Miller Dam)",
      waterTemp: "65°F - 85°F (seasonal)"
    },
    landmarks: [
      "Mount Bonnell - Historic scenic overlook (775 ft elevation)",
      "Pennybacker Bridge (360 Bridge) - Iconic Austin landmark",
      "Laguna Gloria - Contemporary art museum with waterfront",
      "Red Bud Isle - Dog-friendly park and beach area",
      "West Lake Hills shoreline - Luxury homes and estates",
      "Commons Ford Ranch Metropolitan Park - 215 acres of trails",
      "Mayfield Park & Preserve - Peacocks and historic gardens"
    ],
    fish: [
      "Largemouth Bass - Most popular game fish",
      "Guadalupe Bass (Texas state fish) - Native species",
      "Catfish - Channel and Flathead varieties",
      "Sunfish - Bluegill and Redear",
      "White Bass - Seasonal runs",
      "Crappie - Black and White",
      "Carp - Common in shallower areas"
    ],
    celebrities: [
      "Matthew McConaughey - Longtime Austin resident, Lake Austin home",
      "Sandra Bullock - Owns property near the lake",
      "Lance Armstrong - Former residence on Lake Austin",
      "Michael Dell (Dell Technologies founder) - Lake Austin estate",
      "Whole Foods CEO John Mackey - Area resident",
      "Willie Nelson - Austin music legend, nearby Pedernales",
      "Gary Clark Jr. - Austin blues guitarist"
    ],
    stories: [
      "🎬 The lake has been featured in numerous films including 'Dazed and Confused' (1993)",
      "🎸 Austin's live music scene influences - many musicians have lake houses for creative retreats",
      "🏛️ Original name was 'Colorado River' before damming created the Highland Lakes chain",
      "🌳 The lake is part of the 7-lake chain that protects Austin from flooding",
      "🦇 Congress Avenue Bridge nearby hosts 1.5 million Mexican free-tailed bats (largest urban colony)",
      "🎭 Laguna Gloria hosts outdoor art installations and events year-round",
      "🚣 The lake hosts annual rowing regattas and paddleboard races",
      "🌅 Sunset watching from assets is a beloved Austin tradition, especially near Pennybacker Bridge"
    ],
    activities: [
      "Asset tours and rentals (pontoons, kayaks, paddleboards)",
      "Swimming and cliff jumping (designated areas)",
      "Hiking trails at surrounding parks",
      "Fishing (Texas fishing license required)",
      "Wildlife watching - herons, osprey, turtles",
      "Photography - especially sunrise/sunset",
      "Picnicking at waterfront parks",
      "Stand-up paddleboarding and kayaking"
    ],
    safety: [
      "⚠️ No-wake zones in residential areas - strictly enforced",
      "🚦 Watch for swimmers and paddlers, especially near parks",
      "☀️ High sun exposure - bring sunscreen and water",
      "🌊 Water levels can fluctuate - check LCRA updates",
      "🚨 Emergency: Call 911 or Lake Austin Park Rangers",
      "📱 Download \"Lake Austin Navigator\" app for depth charts",
      "🎣 Fishing license required - purchase at tacklelakeshop.com or TPWD",
      "⛽ Limited fuel stations - plan refueling at lakeside locations"
    ],
    navigationRules: {
      title: "Navigation Rules (COLREGs)",
      description: "International and inland navigation rules to prevent collisions on the water",
      links: [
        {
          name: "US Inland Navigation Rules",
          url: "https://www.navcen.uscg.gov/sites/default/files/pdf/navRules/navrules.pdf",
          description: "Official USCG inland navigation rules for US waters"
        },
        {
          name: "Texas Water Safety Act",
          url: "https://tpwd.texas.gov/regulations/outdoor-annual/asseting/water-safety-act/",
          description: "Texas-specific asseting laws and safety requirements"
        },
        {
          name: "Navigation Rules Quick Reference",
          url: "https://www.asset-ed.com/texas/studyGuide/Navigation-Rules/10104601_700046996/",
          description: "Quick reference guide for asseting rules of the road"
        }
      ]
    },
    essentialKnots: [
      {
        name: "Bowline",
        use: "Creates a fixed loop - perfect for securing to dock cleats",
        videoUrl: "https://www.youtube.com/watch?v=Q74G_uGBo5A",
        difficulty: "Essential"
      },
      {
        name: "Cleat Hitch",
        use: "The proper way to secure a asset to a dock cleat",
        videoUrl: "https://www.youtube.com/watch?v=qfHd8pVZ5c8",
        difficulty: "Essential"
      },
      {
        name: "Clove Hitch",
        use: "Quick temporary fastening to a post or rail",
        videoUrl: "https://www.youtube.com/watch?v=I-lRHPTCJtg",
        difficulty: "Essential"
      },
      {
        name: "Figure Eight",
        use: "Stopper knot to prevent rope from slipping through",
        videoUrl: "https://www.youtube.com/watch?v=DRoAKKCErpM",
        difficulty: "Basic"
      },
      {
        name: "Round Turn & Two Half Hitches",
        use: "Securing fenders or attaching to a ring",
        videoUrl: "https://www.youtube.com/watch?v=7jRHRCkBwpM",
        difficulty: "Useful"
      }
    ]
  };

  return (
    <DashboardPageLayout
      title="Lake Austin Information"
      subtitle="Essential water conditions, landmarks, and navigation resources"
    >
      <div className="space-y-4">
      {/* Quick Stats Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Waves className="w-5 h-5 text-blue-600" />
            Water Info - Lake Austin
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {Object.entries(lakeInfo.overview).map(([key, value]) => (
              <div key={key} className="text-center p-3 bg-blue-50 rounded-lg">
                <p className="text-sm text-gray-600 capitalize">{key.replace(/([A-Z])/g, ' $1')}</p>
                <p className="text-lg font-semibold text-blue-900">{value}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Map Link Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="w-5 h-5 text-green-600" />
            Interactive Navigation Map
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-600 mb-4">
            Access detailed GPS marine charts with depth contours, navigation markers, and points of interest.
          </p>
          <Button
            onClick={() => window.open('https://fishing-app.gpsnauticalcharts.com/i-asseting-fishing-web-app/fishing-marine-charts-navigation.html?title=Austin+asseting+app#13/30.3400/-97.8550', '_blank')}
            className="w-full md:w-auto"
            style={{ background: '#2563EB' }}
          >
            <ExternalLink className="w-4 h-4 mr-2" />
            Open Lake Austin GPS Map
          </Button>
        </CardContent>
      </Card>

      {/* Landmarks */}
      <Card>
        <CardHeader className="cursor-pointer" onClick={() => toggleSection('landmarks')}>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Landmark className="w-5 h-5 text-purple-600" />
              Landmarks & Points of Interest
            </div>
            {expandedSections.landmarks ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </CardTitle>
        </CardHeader>
        {expandedSections.landmarks && (
          <CardContent>
            <ul className="space-y-2">
              {lakeInfo.landmarks.map((landmark, idx) => (
                <li key={idx} className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 mt-1 text-purple-600 flex-shrink-0" />
                  <span className="text-gray-700">{landmark}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        )}
      </Card>

      {/* Fish & Wildlife */}
      <Card>
        <CardHeader className="cursor-pointer" onClick={() => toggleSection('wildlife')}>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Fish className="w-5 h-5 text-teal-600" />
              Fish Species & Wildlife
            </div>
            {expandedSections.wildlife ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </CardTitle>
        </CardHeader>
        {expandedSections.wildlife && (
          <CardContent>
            <div className="space-y-4">
              <div>
                <h4 className="font-semibold mb-2 text-teal-900">Common Fish Species:</h4>
                <ul className="space-y-1">
                  {lakeInfo.fish.map((fish, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <Fish className="w-4 h-4 mt-1 text-teal-600 flex-shrink-0" />
                      <span className="text-gray-700">{fish}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="font-semibold mb-2 text-teal-900">Activities:</h4>
                <ul className="space-y-1">
                  {lakeInfo.activities.map((activity, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <span className="text-teal-600 mr-2">•</span>
                      <span className="text-gray-700">{activity}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Celebrities */}
      <Card>
        <CardHeader className="cursor-pointer" onClick={() => toggleSection('celebrities')}>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Star className="w-5 h-5 text-yellow-600" />
              Notable Residents & Celebrities
            </div>
            {expandedSections.celebrities ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </CardTitle>
        </CardHeader>
        {expandedSections.celebrities && (
          <CardContent>
            <ul className="space-y-2">
              {lakeInfo.celebrities.map((celeb, idx) => (
                <li key={idx} className="flex items-start gap-2">
                  <Star className="w-4 h-4 mt-1 text-yellow-600 flex-shrink-0" />
                  <span className="text-gray-700">{celeb}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        )}
      </Card>

      {/* Stories & Fun Facts */}
      <Card>
        <CardHeader className="cursor-pointer" onClick={() => toggleSection('stories')}>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Waves className="w-5 h-5 text-indigo-600" />
              Stories & Fun Facts
            </div>
            {expandedSections.stories ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </CardTitle>
        </CardHeader>
        {expandedSections.stories && (
          <CardContent>
            <ul className="space-y-3">
              {lakeInfo.stories.map((story, idx) => (
                <li key={idx} className="text-gray-700 leading-relaxed">
                  {story}
                </li>
              ))}
            </ul>
          </CardContent>
        )}
      </Card>

      {/* Safety Tips */}
      <Card className="border-2 border-orange-200 bg-orange-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-orange-900">
            <Ruler className="w-5 h-5" />
            Safety & Regulations
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2">
            {lakeInfo.safety.map((tip, idx) => (
              <li key={idx} className="text-gray-800">
                {tip}
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      {/* Navigation Rules (COLREGs) */}
      <Card className="border-2 border-blue-200 bg-blue-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-blue-900">
            <Compass className="w-5 h-5" />
            {lakeInfo.navigationRules.title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-700 mb-4">{lakeInfo.navigationRules.description}</p>
          <div className="space-y-3">
            {lakeInfo.navigationRules.links.map((link, idx) => (
              <a
                key={idx}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="block p-3 bg-white rounded-lg border border-blue-200 hover:border-blue-400 hover:shadow-md transition-all"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <ExternalLink className="w-4 h-4 text-blue-600" />
                      <span className="font-semibold text-blue-900">{link.name}</span>
                    </div>
                    <p className="text-sm text-gray-600">{link.description}</p>
                  </div>
                </div>
              </a>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Essential Knots */}
      <Card className="border-2 border-green-200 bg-green-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-green-900">
            <LinkIcon className="w-5 h-5" />
            Essential Asseting Knots
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-700 mb-4">
            Master these essential knots for safe and efficient asset operations. Click any knot to watch a tutorial video.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {lakeInfo.essentialKnots.map((knot, idx) => (
              <a
                key={idx}
                href={knot.videoUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="block p-4 bg-white rounded-lg border border-green-200 hover:border-green-400 hover:shadow-md transition-all"
              >
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <LinkIcon className="w-5 h-5 text-green-700" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-semibold text-green-900">{knot.name}</span>
                      <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full">
                        {knot.difficulty}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">{knot.use}</p>
                    <div className="flex items-center gap-1 text-xs text-blue-600">
                      <ExternalLink className="w-3 h-3" />
                      <span>Watch Tutorial</span>
                    </div>
                  </div>
                </div>
              </a>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
    </DashboardPageLayout>
  );
};

export default LakeAustinInfo;
